import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Switch,
  IconButton,
  Paper,
  Slider,
  TextField,
  Select,
  MenuItem,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Grid,
  Tooltip,
  Divider,
  styled,
  Chip,
  CircularProgress,
  Alert,
  Snackbar,
  Link,
} from '@mui/material';
import {
  DragIndicator as DragIndicatorIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Send as SendIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { format, parse } from 'date-fns';

import reminderService from '../services/reminderService';
import PhoneNumberSelect, { PhoneChannel, fetchPhoneChannels } from './PhoneNumberSelect';

// Styled components
const Section = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const ReminderStep = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

interface ReminderStep {
  id: number;
  communication_method: 'sms' | 'call';
  fallback_delay_days: number;
}

interface ReminderSettings {
  days_before_appointment: number;
  preferred_time_of_day: string;
  steps: ReminderStep[];
  message_template: string;
  default_outbound_number: string;
}

interface ReminderAgent {
  name: string;
  note: string;
  synth_id: number | null;
  synth_agent_id: number;
  default_outbound_number: string;
}

interface ReminderSettingsResponse {
  results: {
    reminder_settings: ReminderSettings | null;
    reminder_agent: ReminderAgent;
  };
  status: number;
  success: boolean;
}

interface ReminderSettingsProps {}

export const ReminderSettings: React.FC<ReminderSettingsProps> = () => {
  const getBaseUrl = (url: string): string => {
    try {
      const urlObj = new URL(url);
      return `${urlObj.protocol}//${urlObj.host}`;
    } catch (e) {
      return url; // Return original URL if parsing fails
    }
  };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isDefaultSettings, setIsDefaultSettings] = useState(false);
  const [daysBeforeAppointment, setDaysBeforeAppointment] = useState<number | null>(null);
  const [preferredHour, setPreferredHour] = useState<number>(9);
  const [preferredMinute, setPreferredMinute] = useState<number>(0);
  const [steps, setSteps] = useState<ReminderStep[]>([
    { id: 1, communication_method: 'sms', fallback_delay_days: 0 },
    { id: 2, communication_method: 'call', fallback_delay_days: 1 }
  ]);
  const [messageTemplate, setMessageTemplate] = useState<string>('');
  const [reminderAgent, setReminderAgent] = useState<ReminderAgent | null>(null);
  const [reminderAgentNote, setReminderAgentNote] = useState<string>('');
  const [defaultOutboundNumber, setDefaultOutboundNumber] = useState<string>('');
  const [channels, setChannels] = useState<PhoneChannel[]>([]);

  const loadReminderSettings = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await reminderService.getReminderSettings();

      // Always set the agent info
      setReminderAgent(response.results.reminder_agent);
      setReminderAgentNote(response.results.reminder_agent.note);
      setDefaultOutboundNumber(response.results.reminder_agent.default_outbound_number);

      if (response.results.reminder_settings === null) {
        // Only set defaults if settings are completely absent (null)
        setIsDefaultSettings(true);
        setDaysBeforeAppointment(3);
        setPreferredHour(9);
        setPreferredMinute(0);
        setSteps([
          { id: 1, communication_method: 'sms', fallback_delay_days: 0 },
          { id: 2, communication_method: 'call', fallback_delay_days: 1 }
        ]);
        setMessageTemplate('Your appointment is coming up!');
      } else {
        setIsDefaultSettings(false);
        // Use existing settings even if empty
        setDaysBeforeAppointment(response.results.reminder_settings.days_before_appointment);
        // Parse time string
        const [hours, minutes] = response.results.reminder_settings.preferred_time_of_day.split(':');
        setPreferredHour(parseInt(hours, 10));
        setPreferredMinute(parseInt(minutes, 10));
        setSteps(response.results.reminder_settings.steps || []);
        setMessageTemplate(response.results.reminder_settings.message_template || '');
        setDefaultOutboundNumber(response.results.reminder_settings.default_outbound_number);
      }
    } catch (err) {
      setError('Failed to load settings. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReminderSettings();
  }, [loadReminderSettings]);

  useEffect(() => {
    // Load channels from cache or fetch them
    fetchPhoneChannels()
      .then(setChannels)
      .catch(error => console.error('Error loading phone channels:', error));
  }, []);

  const handleAddStep = () => {
    const newId = steps.length > 0 ? Math.max(...steps.map(s => s.id)) + 1 : 1;
    const prevStepDelay = steps.length > 0 ? steps[steps.length - 1].fallback_delay_days : 0;
    setSteps([
      ...steps,
      {
        id: newId,
        communication_method: 'sms',
        fallback_delay_days: prevStepDelay // New step gets same delay as previous step
      }
    ]);
  };

  const handleDeleteStep = (stepId: number) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;
    
    const newSteps = steps.filter((_, index) => index !== stepIndex).map((step, index) => ({
      ...step,
      id: index + 1
    }));
    setSteps(newSteps);
  };

  const handleDelayChange = (stepId: number, newDelay: number) => {
    const stepIndex = steps.findIndex(s => s.id === stepId);
    if (stepIndex === -1) return;

    // Check if the new delay is valid (>= previous step's delay)
    if (stepIndex > 0 && newDelay < steps[stepIndex - 1].fallback_delay_days) {
      return; // Don't allow setting a delay less than the previous step
    }

    const newSteps = [...steps];
    newSteps[stepIndex] = { ...newSteps[stepIndex], fallback_delay_days: newDelay };

    // Update subsequent steps that now have invalid delays
    for (let i = stepIndex + 1; i < newSteps.length; i++) {
      if (newSteps[i].fallback_delay_days < newDelay) {
        newSteps[i] = { ...newSteps[i], fallback_delay_days: newDelay };
      }
    }

    setSteps(newSteps);
  };

  const handleSave = async () => {
    if (daysBeforeAppointment === null) {
      setError('Days before appointment is required');
      return;
    }

    if (!defaultOutboundNumber) {
      setError('Default outbound number is required');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await reminderService.updateReminderSettings({
        days_before_appointment: daysBeforeAppointment,
        preferred_time_of_day: `${preferredHour.toString().padStart(2, '0')}:${preferredMinute.toString().padStart(2, '0')}:00`,
        steps: steps.map(step => ({
          id: step.id,
          communication_method: step.communication_method,
          fallback_delay_days: step.fallback_delay_days
        })),
        message_template: messageTemplate,
        default_outbound_number: defaultOutboundNumber,
      });
      setSuccess('Settings saved successfully');
    } catch (err) {
      setError('Failed to save settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    
    // Get the original delays in order
    const originalDelays = steps.map(step => step.fallback_delay_days);
    
    // Create new array with reordered steps
    const items = Array.from(steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    
    // Update steps with new order but maintain the original delay sequence
    setSteps(items.map((item, index) => ({
      ...item,
      id: index + 1,
      fallback_delay_days: originalDelays[index]
    })));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, margin: '0 auto', padding: 3 }}>
      {isDefaultSettings && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }}
          icon={<HelpIcon />}
        >
          You are viewing default reminder settings. No settings have been saved yet. Your changes will be saved when you click "Save Settings".
        </Alert>
      )}
      
      <Section>
        <SectionTitle>
          <Typography variant="h5" component="h1" sx={{ flexGrow: 1 }}>
            Appointment Reminder Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            Reminder Agent: <Link 
              href={`${getBaseUrl(localStorage.getItem('source_url') || 'https://app.yib.io')}/main/workshop/${reminderAgent?.synth_id || ''}/configure`}
              target="_blank"
              rel="noopener"
              sx={{ 
                color: 'inherit',
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
              }}
            ><strong>{reminderAgent?.name} </strong>
            {reminderAgentNote && (
              <Typography component="span" color="text.secondary">
                ({reminderAgentNote})
              </Typography>
            )}</Link>
          </Typography>
        </SectionTitle>
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Reminder Timing</Typography>
          <Tooltip title="Configure when appointment reminders should be sent.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              type="number"
              label="Days Before Appointment"
              value={daysBeforeAppointment === null ? '' : daysBeforeAppointment}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value);
                if (value === null || isNaN(value)) {
                  setDaysBeforeAppointment(null);
                } else {
                  setDaysBeforeAppointment(Math.max(1, value));
                }
              }}
              inputProps={{
                min: 1,
              }}
              helperText="Enter the number of days before the appointment to start reminders"
              error={daysBeforeAppointment === null}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Preferred Time"
              type="time"
              value={`${preferredHour.toString().padStart(2, '0')}:${preferredMinute.toString().padStart(2, '0')}`}
              onChange={(e) => {
                const [hours, minutes] = e.target.value.split(':');
                setPreferredHour(parseInt(hours, 10));
                setPreferredMinute(parseInt(minutes, 10));
              }}
              inputProps={{
                step: 60
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <PhoneNumberSelect
              label="Default Outbound Number"
              value={defaultOutboundNumber}
              onChange={(value) => setDefaultOutboundNumber(value)}
              channels={channels}
            />
            {!defaultOutboundNumber && (
              <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                Default outbound number is required
              </Typography>
            )}
          </Grid>
        </Grid>
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Reminder Steps</Typography>
          <Tooltip title="Configure the sequence of reminder steps. You can add multiple steps with different communication methods.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="reminder-steps">
            {(provided) => (
              <div {...provided.droppableProps} ref={provided.innerRef}>
                {steps.map((step, index) => (
                  <Draggable 
                    key={`step-${index}`} 
                    draggableId={`step-${index}`} 
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <ReminderStep
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        elevation={snapshot.isDragging ? 3 : 1}
                        sx={{
                          ...provided.draggableProps.style,
                          backgroundColor: snapshot.isDragging ? 'background.paper' : undefined,
                        }}
                      >
                        <div {...provided.dragHandleProps}>
                          <DragIndicatorIcon sx={{ color: 'text.secondary', cursor: 'grab' }} />
                        </div>
                        <Typography variant="body1" sx={{ minWidth: 30 }}>
                          {index + 1}.
                        </Typography>
                        <FormControl sx={{ minWidth: 200 }}>
                          <InputLabel>Communication Method</InputLabel>
                          <Select
                            value={step.communication_method}
                            label="Communication Method"
                            onChange={(e) => {
                              const newSteps = [...steps];
                              newSteps[index] = {
                                ...newSteps[index],
                                communication_method: e.target.value as 'sms' | 'call'
                              };
                              setSteps(newSteps);
                            }}
                          >
                            <MenuItem value="sms">SMS</MenuItem>
                            <MenuItem value="call">Call</MenuItem>
                          </Select>
                        </FormControl>
                        
                        <FormControl sx={{ minWidth: 200 }}>
                          <InputLabel>Followup Interval</InputLabel>
                          <Select
                            value={step.fallback_delay_days}
                            label="Followup Interval"
                            onChange={(e) => handleDelayChange(step.id, Number(e.target.value))}
                          >
                            {index === 0 ? (
                              <MenuItem value={0}>Initial Reminder (0 days)</MenuItem>
                            ) : (
                              // For non-first steps, only show options >= previous step's delay
                              Array.from({ length: 6 }, (_, i) => i)
                                .filter(days => days >= steps[index - 1].fallback_delay_days)
                                .map(days => (
                                  <MenuItem key={days} value={days}>
                                    {days === 0 ? 'Initial Reminder (0 days)' : `${days} days after initial`}
                                  </MenuItem>
                                ))
                            )}
                          </Select>
                        </FormControl>

                        <IconButton
                          onClick={() => handleDeleteStep(step.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ReminderStep>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddStep}
          variant="outlined"
          sx={{ mt: 2 }}
        >
          Add Step
        </Button>
      </Section>

      <Section>
        <SectionTitle>
          <Typography variant="h6">Message Template</Typography>
          <Tooltip title="Configure the message template for reminders. You can use variables to personalize the message. Variables will be filled out with the appointment details when the reminder is sent.">
            <IconButton size="small">
              <HelpIcon />
            </IconButton>
          </Tooltip>
        </SectionTitle>

        <TextField
          fullWidth
          multiline
          rows={4}
          value={messageTemplate}
          onChange={(e) => setMessageTemplate(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Typography variant="subtitle2" gutterBottom>
          Available Variables <Typography variant="caption" component="span" color="text.secondary">(Click to insert in your message)</Typography><br/><br/>
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
          {[
            { display: 'Appointment Date', value: '{appointmentDate}' },
            { display: 'Appointment Time', value: '{appointmentTime}' },
            { display: 'Patient Name', value: '{patientName}' },
            { display: 'Provider Name', value: '{providerName}' }
          ].map((variable) => (
            <Chip
              key={variable.value}
              label={variable.display}
              variant="outlined"
              onClick={() => {
                const textarea = document.querySelector('textarea');
                if (textarea) {
                  const start = textarea.selectionStart;
                  const end = textarea.selectionEnd;
                  const text = textarea.value;
                  const newText = text.substring(0, start) + variable.value + text.substring(end);
                  setMessageTemplate(newText);
                  // Set cursor position after the inserted variable
                  setTimeout(() => {
                    textarea.selectionStart = start + variable.value.length;
                    textarea.selectionEnd = start + variable.value.length;
                    textarea.focus();
                  }, 0);
                }
              }}
              sx={{ cursor: 'pointer' }}
            />
          ))}
        </Stack>
      </Section>

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          startIcon={<SaveIcon />}
          variant="contained"
          onClick={handleSave}
          disabled={loading}
        >
          Save Settings
        </Button>
      </Stack>

      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReminderSettings; 